import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO';
import SitePage, { CallToAction, styles, T, ACCENT } from '../../components/site/SitePage';
import { CHANNELS, CONSEIL_SEMAINES, EXAMPLE, MAX_WEEKS, SERVICE_LEVELS, compute, project, toExcel, num } from './stockSecurite';
import { modeleStock, MODELE_NOM } from './modeleStock';
import './outils.css';

const PATH = '/outils/calcul-stock-de-securite';
// Date de dernière mise à jour du contenu : visible sur la page et lue par Google (dateModified).
// À changer à chaque modification réelle du calcul ou du texte.
const MAJ = '2026-10-08';
const majFr = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${MAJ}T12:00:00`));

const WARN = '#B45309';
const RED = '#C2410C';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const BAR = [ACCENT, '#9AA6C8', '#D3D9EA'];
const emptyWeek = () => ['', '', ''];

const nf = (digits) => new Intl.NumberFormat('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const n0 = (v) => nf(0).format(v);
const n1 = (v) => nf(1).format(v);
const n2 = (v) => nf(2).format(v);
const n3 = (v) => nf(3).format(v);
const days = (n) => { const k = Math.round(n); return `${n0(k)} ${k >= 2 ? 'jours' : 'jour'}`; };
const eur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

// Chiffres de l'exemple, repris dans le texte : ils viennent du même calcul que l'outil.
const EX = compute(EXAMPLE);

const FAQ = [
  ['Quel niveau de service choisir ?', "95 % convient à la plupart des produits : une rupture possible tous les vingt cycles de commande. Montez à 98 ou 99 % sur les best-sellers, dont une rupture fait aussi perdre le classement Amazon. Descendez à 90 % sur le fonds de catalogue, où chaque unité en trop coûte du stockage."],
  ["Stock de sécurité et stock d'alerte, c'est la même chose ?", "Non. Le stock d'alerte est un autre nom du point de commande : le niveau où l'on repasse commande. Il comprend le stock de sécurité, plus tout ce que vous vendrez pendant le délai d'approvisionnement."],
  ["Combien de semaines d'historique prendre ?", "Huit à treize semaines récentes suffisent pour un produit stable. Retirez les semaines de rupture : des ventes à zéro faute de stock font baisser la moyenne et monter l'écart-type, et le calcul se trompe dans les deux sens."],
  ["J'ai du stock chez Amazon et dans mon entrepôt : je les additionne ?", "Pour la commande au fournisseur, oui : c'est le stock total qui compte. Dans Seller Central, ajoutez au stock disponible le stock réservé, celui en transfert et les envois en route vers Amazon. L'envoi vers les entrepôts Amazon est un second calcul, avec son propre délai."],
  ['Pourquoi le B2B ne compte-t-il pas dans la variabilité ?', "Parce que les commandes des revendeurs arrivent par gros volumes, souvent connus à l'avance. Les compter comme du hasard ferait exploser le stock de sécurité. Le calculateur les garde dans la moyenne des ventes, mais calcule la variabilité sur Amazon et le site seulement."],
  ["Comment calculer le stock de sécurité sans écart-type ?", "La méthode simple : (ventes maximales par jour × délai maximal) − (ventes moyennes par jour × délai moyen). Elle se fait de tête, mais elle surestime souvent, car elle suppose que le pic de ventes et le pire retard tombent en même temps."]
];

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

const APP_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculateur de stock de sécurité et de point de commande',
  url: `https://autoria.dev${PATH}`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  inLanguage: 'fr-FR',
  isAccessibleForFree: true,
  dateModified: MAJ,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  author: { '@type': 'Person', name: 'Mattéo', url: 'https://autoria.dev/about' }
};

const card = { background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 10 };
const stepTitle = { margin: 0, fontSize: 18, fontWeight: 800, fontStretch: '88%' };
const hint = { margin: 0, fontSize: 14, color: T.muted, lineHeight: 1.45 };

const Field = ({ id, label, value, onChange, unit, help }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600 }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input id={id} className="ou-input" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
      {unit && <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint, whiteSpace: 'nowrap' }}>{unit}</span>}
    </div>
    {help && <p style={{ ...hint, fontSize: 13 }}>{help}</p>}
  </div>
);

// Courbe du stock physique projeté, avec le point de commande et le stock de sécurité.
const Projection = ({ r, proj }) => {
  const W = 480;
  const H = 200;
  const pad = { l: 8, r: 8, t: 12, b: 22 };
  const top = Math.max(r.reorder, ...proj.points) * 1.12;
  const x = (t) => pad.l + (t / proj.horizon) * (W - pad.l - pad.r);
  const y = (v) => pad.t + (1 - v / top) * (H - pad.t - pad.b);
  const line = proj.points.map((v, t) => `${t ? 'L' : 'M'}${x(t).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(proj.horizon).toFixed(1)} ${y(0)} L${x(0)} ${y(0)} Z`;
  const ticks = [];
  for (let t = 0; t <= proj.horizon; t += proj.horizon > 150 ? 60 : 30) ticks.push(t);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Projection du stock dans les prochains mois" style={{ width: '100%', height: 'auto', display: 'block' }}>
      <path d={area} fill="#F1F4FF" />
      <line x1={pad.l} x2={W - pad.r} y1={y(r.reorder)} y2={y(r.reorder)} stroke={WARN} strokeDasharray="5 4" strokeWidth="1.5" />
      <line x1={pad.l} x2={W - pad.r} y1={y(r.safety)} y2={y(r.safety)} stroke={RED} strokeDasharray="2 3" strokeWidth="1.5" />
      <path d={line} fill="none" stroke={ACCENT} strokeWidth="2.5" strokeLinejoin="round" />
      {proj.orders.map((o) => (
        <g key={`o${o.t}`}>
          <line x1={x(o.t)} x2={x(o.t)} y1={pad.t} y2={y(0)} stroke={WARN} strokeWidth="1" opacity="0.5" />
          <circle cx={x(o.t)} cy={y(proj.points[o.t])} r="4.5" fill="#FFFFFF" stroke={WARN} strokeWidth="2" />
        </g>
      ))}
      {proj.arrivals.map((a) => <circle key={`a${a.t}`} cx={x(a.t)} cy={y(proj.points[a.t])} r="4.5" fill={ACCENT} stroke="#FFFFFF" strokeWidth="2" />)}
      {proj.stockout !== null && <circle cx={x(proj.stockout)} cy={y(0)} r="5" fill={RED} stroke="#FFFFFF" strokeWidth="2" />}
      <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} stroke={T.line2} />
      {ticks.map((t) => <text key={t} x={x(t)} y={H - 6} fontSize="12" fill={T.faint} fontFamily="IBM Plex Mono, monospace" textAnchor={t === 0 ? 'start' : 'middle'}>{t === 0 ? "Aujourd'hui" : `J+${t}`}</text>)}
    </svg>
  );
};

const Legend = ({ color, dashed, dot, children }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    {dot
      ? <span style={{ width: 10, height: 10, borderRadius: '50%', background: dot === 'hollow' ? '#FFFFFF' : color, border: `2px solid ${color}`, boxSizing: 'border-box' }} />
      : <span style={{ width: 16, borderTop: `2px ${dashed ? 'dashed' : 'solid'} ${color}` }} />}
    {children}
  </span>
);

const Status = ({ r, proj }) => {
  let tone = ACCENT;
  let title = `Prochaine commande dans ${days(r.daysToOrder)}`;
  let text = `${n0(r.qty)} unités, de quoi couvrir ${days(r.orderCover)} de ventes.`;
  if (proj.stockout !== null) {
    tone = RED;
    title = `Rupture prévue dans ${days(proj.stockout)}`;
    const perte = `Environ ${days(proj.stockoutDays)} sans stock, soit ${n0(proj.lost)} ventes perdues${r.cost ? ` (${eur(proj.lost * r.cost)} de marchandise au prix d'achat)` : ''}.`;
    text = r.daysToOrder === 0
      ? `Commandez ${n0(r.qty)} unités maintenant : elles arriveront dans ${days(r.lead)}, trop tard pour tout éviter. ${perte} Un envoi plus rapide pour une partie limiterait la casse.`
      : `Le stock déjà commandé n'arrive pas à temps. ${perte} Commande suivante dans ${days(r.daysToOrder)} : ${n0(r.qty)} unités.`;
  } else if (r.daysToOrder === 0) {
    tone = WARN;
    title = `À commander maintenant : ${n0(r.qty)} unités`;
    text = `Votre stock (${n0(r.position)}) est passé sous le point de commande (${n0(r.reorder)}).`;
  }
  return (
    <div role="status" style={{ borderLeft: `4px solid ${tone}`, background: '#FFFFFF', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p style={{ margin: 0, fontSize: 20, fontWeight: 800, fontStretch: '88%', color: tone }}>{title}</p>
      <p style={{ margin: 0, fontSize: 15, color: T.text }}>{text}</p>
    </div>
  );
};

const Kpi = ({ label, value, unit, sub, big }) => (
  <div style={{ padding: '14px 16px', background: '#FFFFFF', display: 'flex', flexDirection: 'column', gap: 2 }}>
    <span style={{ fontFamily: T.mono, fontSize: 12, color: T.faint }}>{label}</span>
    <span style={{ fontSize: big ? 40 : 28, fontWeight: 800, fontStretch: '80%', lineHeight: 1.05, fontVariantNumeric: 'tabular-nums' }}>{value} <span style={{ fontSize: 15, fontWeight: 600, color: T.muted }}>{unit}</span></span>
    {sub && <span style={{ fontSize: 13, color: T.muted }}>{sub}</span>}
  </div>
);

const Calculator = () => {
  const [p, setP] = useState(EXAMPLE);
  const [copied, setCopied] = useState(false);
  const r = useMemo(() => compute(p), [p]);
  const proj = useMemo(() => (r.error ? null : project(r)), [r]);
  // Mesure (après accord seulement) : une fois par visite, dès que le visiteur touche aux chiffres.
  const used = useRef(false);
  const touched = () => {
    if (used.current || !window.autoriaTrack) return;
    used.current = true;
    window.autoriaTrack('calculateur_utilise');
  };
  const set = (key) => (value) => { touched(); setP((prev) => ({ ...prev, [key]: value })); };

  useEffect(() => {
    if (!copied) return undefined;
    const id = setTimeout(() => setCopied(false), 4000);
    return () => clearTimeout(id);
  }, [copied]);

  const setCell = (i, j, value) => { touched(); setCellValue(i, j, value); };
  const setCellValue = (i, j, value) => setP((prev) => {
    const weeks = prev.weeks.map((w) => [...w]);
    weeks[i][j] = value;
    return { ...prev, weeks };
  });

  // Coller plusieurs cellules depuis Excel remplit le tableau à partir de la case visée.
  const onPaste = (i, j) => (e) => {
    const text = e.clipboardData.getData('text');
    if (!/[\t\n;]/.test(text.trim())) return;
    e.preventDefault();
    touched();
    const lines = text.trim().split(/\r?\n/)
      .map((line) => line.split(/\t|;/).map((c) => c.replace(/[\s  ]/g, '')))
      // « 1,450 » venu d'un Excel anglais est un séparateur de milliers, pas une décimale.
      .map((cells) => cells.map((c) => (/^\d{1,3}(,\d{3})+$/.test(c) ? c.replace(/,/g, '') : c)))
      .filter((cells) => cells.some((c) => num(c) !== null));
    setP((prev) => {
      const weeks = prev.weeks.map((w) => [...w]);
      lines.slice(0, MAX_WEEKS - i).forEach((cells, k) => {
        if (!weeks[i + k]) weeks[i + k] = emptyWeek();
        cells.slice(0, 3 - j).forEach((c, m) => { weeks[i + k][j + m] = num(c) === null ? '' : String(num(c)); });
      });
      return { ...prev, weeks };
    });
  };

  const copyExcel = async () => {
    const tsv = toExcel(p);
    try {
      await navigator.clipboard.writeText(tsv);
    } catch {
      const area = document.createElement('textarea');
      area.value = tsv;
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      area.remove();
    }
    setCopied(true);
    if (window.autoriaTrack) window.autoriaTrack('calculateur_copie_excel');
  };

  const totals = p.weeks.map((w) => CHANNELS.reduce((t, _, j) => t + Math.max(0, num(w[j]) || 0), 0));
  const maxTotal = Math.max(1, ...totals);

  return (
    <>
    <div style={{ ...styles.container, display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
      <div style={{ ...card, flex: '1 1 460px', minWidth: 0, padding: 'clamp(16px, 3vw, 28px)', display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 style={stepTitle}>1 · Vos ventes par semaine</h2>
          <p style={hint}>Collez vos chiffres depuis Excel dans la première case : une colonne Amazon, une colonne Shopify, une colonne B2B (revendeurs, grossistes). Un canal que vous n'avez pas reste vide. Laissez de côté les semaines de rupture.</p>
          <div role="table" aria-label="Ventes par semaine" style={{ border: `1px solid ${T.line}`, borderRadius: 6, overflow: 'hidden' }}>
            <div role="row" className="ou-weeks" style={{ padding: '8px 10px', background: '#F7F8FA', borderBottom: `1px solid ${T.line}`, fontFamily: T.mono, fontSize: 12, color: T.faint }}>
              <span role="columnheader">Sem.</span>
              {CHANNELS.map((c) => <span key={c} role="columnheader" style={{ textAlign: 'right' }}>{c}</span>)}
              <span role="columnheader" style={{ textAlign: 'right' }}>Total</span>
              <span className="ou-bar" aria-hidden="true" />
            </div>
            {p.weeks.map((w, i) => (
              <div role="row" key={i} className="ou-weeks" style={{ padding: '2px 10px', borderBottom: i < p.weeks.length - 1 ? `1px solid ${T.line}` : 0 }}>
                <span role="cell" style={{ fontFamily: T.mono, fontSize: 12, color: T.faint }}>S{i + 1}</span>
                {CHANNELS.map((c, j) => (
                  <span key={c} role="cell"><input className="ou-input ou-cell" inputMode="numeric" aria-label={`Semaine ${i + 1}, ventes ${c}`} value={w[j] ?? ''} onChange={(e) => setCell(i, j, e.target.value)} onPaste={onPaste(i, j)} /></span>
                ))}
                <span role="cell" style={{ fontFamily: T.mono, fontSize: 14, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{totals[i] ? n0(totals[i]) : '—'}</span>
                <span role="cell" className="ou-bar" aria-hidden="true" style={{ display: 'flex', height: 8, borderRadius: 2, overflow: 'hidden', width: `${(totals[i] / maxTotal) * 100}%` }}>
                  {CHANNELS.map((c, j) => <span key={c} style={{ flex: Math.max(0, num(w[j]) || 0), background: BAR[j] }} />)}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px', fontSize: 14 }}>
            {p.weeks.length < MAX_WEEKS && <button type="button" className="ou-link" onClick={() => setP((prev) => ({ ...prev, weeks: [...prev.weeks, emptyWeek()] }))}>Ajouter une semaine</button>}
            {p.weeks.length > 2 && <button type="button" className="ou-link" onClick={() => setP((prev) => ({ ...prev, weeks: prev.weeks.slice(0, -1) }))}>Retirer la dernière</button>}
            <button type="button" className="ou-link" onClick={() => setP((prev) => ({ ...prev, weeks: Array.from({ length: 12 }, emptyWeek) }))}>Vider le tableau</button>
          </div>
          {!r.error && r.weeks > CONSEIL_SEMAINES && <p role="note" style={{ ...hint, color: '#B45309' }}>{r.weeks} semaines : au-delà de {CONSEIL_SEMAINES}, la saison se mélange aux variations et gonfle le stock de sécurité. Gardez plutôt les {CONSEIL_SEMAINES} dernières.</p>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2 style={stepTitle}>2 · Votre délai d'approvisionnement</h2>
          <p style={hint}>Du bon de commande au produit vendable. Pour Amazon, comptez la réception par leurs entrepôts : une à trois semaines en fin d'année.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14 }}>
            <Field id="ou-prod" label="Fabrication" unit="jours" value={p.production} onChange={set('production')} />
            <Field id="ou-trans" label="Transport" unit="jours" value={p.transport} onChange={set('transport')} />
            <Field id="ou-rec" label="Réception entrepôt ou Amazon" unit="jours" value={p.reception} onChange={set('reception')} />
          </div>
          <p style={{ ...hint, fontSize: 14 }}>Sur vos dix dernières commandes à ce fournisseur, du bon de commande à la mise en vente :</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14 }}>
            <Field id="ou-lmin" label="Délai total le plus court" unit="jours" value={p.leadMin} onChange={set('leadMin')} />
            <Field id="ou-lmax" label="Délai total le plus long" unit="jours" value={p.leadMax} onChange={set('leadMax')} help="Le Nouvel An chinois ajoute environ trois semaines aux commandes de décembre à février." />
          </div>
          {!r.error && <p style={{ margin: 0, fontFamily: T.mono, fontSize: 14 }}>Délai total : <strong>{days(r.lead)}</strong> · variabilité : <strong>{n1(r.leadSd)} jours</strong> d'écart-type</p>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h2 style={stepTitle}>3 · Votre stock et vos règles</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 14 }}>
            <Field id="ou-stock" label="Stock possédé" unit="unités" value={p.stock} onChange={set('stock')} help="Entrepôt + Amazon (disponible, réservé, en transfert) + envois en route vers Amazon." />
            <Field id="ou-transit" label="Commandé au fournisseur, pas encore reçu" unit="unités" value={p.transit} onChange={set('transit')} />
            {(num(p.transit) || 0) > 0 && <Field id="ou-transit-in" label="Arrivée de cette commande" unit="jours" value={p.transitIn} onChange={set('transitIn')} />}
            <Field id="ou-cycle" label="Période couverte par chaque commande" unit="jours" value={p.cycle} onChange={set('cycle')} help="Combien de jours de ventes chaque commande doit couvrir." />
            <Field id="ou-season" label="Hausse attendue sur cette période" unit="%" value={p.season} onChange={set('season')} help="Par exemple 60 si la commande doit tenir jusqu'à Noël. Les ventes d'aujourd'hui ne changent pas." />
            <Field id="ou-moq" label="Minimum de commande" unit="unités" value={p.moq} onChange={set('moq')} help="Facultatif." />
            <Field id="ou-pack" label="Colisage" unit="u / carton" value={p.pack} onChange={set('pack')} help="Facultatif : la quantité est arrondie au carton." />
            <Field id="ou-cost" label="Coût d'achat rendu" unit="€ HT / u" value={p.cost} onChange={set('cost')} help="Facultatif : pour chiffrer ce que le stock immobilise." />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span id="ou-service" style={{ fontSize: 14, fontWeight: 600 }}>Niveau de service : la probabilité de ne pas rompre avant la livraison</span>
            <div className="ou-seg" role="group" aria-labelledby="ou-service">
              {SERVICE_LEVELS.map((l) => (
                <button key={l.value} type="button" aria-pressed={p.service === l.value} onClick={() => set('service')(l.value)}>{l.label}</button>
              ))}
            </div>
            {!r.error && <p style={{ ...hint, fontSize: 13 }}>{r.ruptureMonths ? `À ${r.serviceLabel}, avec une commande tous les ${days(r.cycle)}, environ une rupture tous les ${n0(r.ruptureMonths)} mois. ` : ''}{r.next ? `Passer à ${r.next.label} : +${n0(r.next.extra)} unités de stock de sécurité${r.cost ? `, soit +${eur(r.next.extra * r.cost)}` : ''}.` : ''}</p>}
          </div>
        </div>
      </div>

      <div className="ou-sticky" style={{ flex: '1 1 420px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {r.error ? (
          <div style={{ ...card, padding: 24 }}><p style={{ margin: 0, color: T.muted }}>{r.error}</p></div>
        ) : (
          <>
            <Status r={r} proj={proj} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1, background: T.line2, border: `1px solid ${T.line2}`, borderRadius: 10, overflow: 'hidden' }}>
              <Kpi big label="Stock de sécurité" value={n0(r.safety)} unit="unités" sub={`${days(r.safety / r.d)} de ventes`} />
              <Kpi big label="Point de commande" value={n0(r.reorder)} unit="unités" sub="Repasser commande sous ce niveau" />
              <Kpi label="Couverture du stock en main" value={n0(r.cover)} unit="jours" sub={`${n1(r.d)} ventes par jour`} />
              <Kpi label="Quantité à commander" value={n0(r.qty)} unit="unités" sub={`${r.daysToOrder === 0 ? 'Maintenant' : `Dans ${days(r.daysToOrder)}`}${r.qty !== r.qtyRaw ? ` · ${n0(r.qtyRaw)} avant minimum et colisage` : ''}`} />
            </div>
            {(r.cost > 0 || r.moqMonths) && (
              <div style={{ ...card, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 4, fontSize: 15, color: T.text }}>
                {r.cost > 0 && <span>Le stock de sécurité immobilise <strong>{eur(r.safetyValue)}</strong>, la commande représente <strong>{eur(r.orderValue)}</strong> au prix d'achat.</span>}
                {r.moqMonths && <span>Le minimum de commande représente {n1(r.moqMonths)} mois de ventes : vous commanderez plus, et moins souvent.</span>}
              </div>
            )}
            <div style={{ ...card, padding: '16px 16px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0, fontFamily: T.mono, fontSize: 12, color: T.faint }}>Votre stock dans les {proj.horizon} prochains jours</p>
              <Projection r={r} proj={proj} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 13, color: T.muted }}>
                <Legend color={WARN} dashed>Point de commande</Legend>
                <Legend color={RED} dashed>Stock de sécurité</Legend>
                <Legend color={WARN} dot="hollow">Commande</Legend>
                <Legend color={ACCENT} dot>Réception</Legend>
              </div>
            </div>
            <div style={{ ...card, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <p style={{ margin: 0, fontFamily: T.mono, fontSize: 12, color: T.faint }}>Le calcul avec vos chiffres</p>
              <pre style={{ margin: 0, fontFamily: T.mono, fontSize: 13.5, lineHeight: 1.7, whiteSpace: 'pre-wrap', color: T.ink }}>
                {`Stock de sécurité = z × √(L × σ² + d² × σL²)\n  = ${n3(r.z)} × √(${n0(r.lead)} × ${n2(r.sdDay)}² + ${n2(r.d)}² × ${n1(r.leadSd)}²)\n  = ${n0(r.safety)} unités\nPoint de commande = d × L + stock de sécurité\n  = ${n2(r.d)} × ${n0(r.lead)} + ${n0(r.safety)} = ${n0(r.reorder)} unités`}
              </pre>
              <p style={{ ...hint, fontSize: 14 }}>
                Avec un délai parfaitement fiable, le stock de sécurité tomberait à {n0(r.safetyReliableLead)} unités ({n0(r.safetyReliableLead - r.safety)}{r.cost ? `, soit ${eur((r.safetyReliableLead - r.safety) * r.cost)}` : ''}). Avec des ventes parfaitement régulières, à {n0(r.safetySteadySales)} ({n0(r.safetySteadySales - r.safety)}).
                {r.safety - r.safetyReliableLead > r.safety - r.safetySteadySales ? ' Fiabiliser le délai fournisseur rapporte plus que de mieux prévoir les ventes.' : ' Ce sont les variations des ventes qui dictent la réserve : une meilleure prévision la ferait baisser.'}
              </p>
              <p style={{ ...hint, fontSize: 14 }}>Amazon {n0(r.amazonShare * 100)} %, Shopify {n0(r.shopifyShare * 100)} %{r.b2bShare > 0 ? `, B2B ${n0(r.b2bShare * 100)} %` : ''} des ventes. Les canaux puisent dans le même stock : le calcul porte sur le total. Le B2B compte dans la moyenne, pas dans la variabilité.</p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              <button type="button" onClick={copyExcel} className="lift" style={{ ...styles.button, border: 0, cursor: 'pointer', fontSize: 16, minHeight: 46, padding: '10px 18px' }}>{copied ? 'Copié : collez en A1 dans Excel' : 'Copier pour Excel'}</button>
              <button type="button" className="ou-link" style={{ fontSize: 15 }} onClick={() => setP(EXAMPLE)}>Revenir à l'exemple</button>
            </div>
            <p style={{ ...hint, fontSize: 13 }}>La feuille copiée contient vos chiffres et les formules : elle se recalcule seule dans Excel en français. Pour un fichier complet, avec un onglet catalogue, voyez juste en dessous.</p>
          </>
        )}
      </div>
    </div>
    {/* Hors du bloc à deux colonnes : sinon la colonne de résultats, collante, recouvre le formulaire. */}
    {!r.error && <div style={{ ...styles.container, marginTop: 24 }}><Modele p={p} /></div>}
    </>
  );
};

// Le modèle Excel complet : envoyé par e-mail (Brevo, via /api/modele) et téléchargeable aussitôt.
// Mattéo reçoit une alerte à chaque demande : c'est la porte d'entrée « sans démarchage ».
const Modele = ({ p }) => {
  const [form, setForm] = useState({ email: '', prenom: '', site: '' });
  const [state, setState] = useState('idle');
  const [error, setError] = useState('');
  const started = useRef(Date.now());

  const download = () => {
    const blob = new Blob([modeleStock(p)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = MODELE_NOM;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    if (window.autoriaTrack) window.autoriaTrack('modele_telecharge');
  };

  const submit = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    if (!EMAIL_RE.test(email)) { setError('Indiquez une adresse e-mail valide.'); return; }
    setError('');
    setState('sending');
    let sent = false;
    try {
      const res = await fetch('/api/modele', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, email, p, duree: Date.now() - started.current, provenance: document.referrer || 'direct' })
      });
      if (res.ok) sent = Boolean((await res.json()).sent);
    } catch {
      sent = false;
    }
    setState(sent ? 'sent' : 'failed');
    if (window.autoriaTrack) window.autoriaTrack('modele_demande', { envoye: sent ? 'oui' : 'non' });
  };

  const label = { fontSize: 14, fontWeight: 600 };
  return (
    <section aria-labelledby="ou-modele" style={{ ...card, minWidth: 0, padding: 'clamp(18px, 3vw, 28px)', display: 'flex', flexWrap: 'wrap', gap: '20px 40px', borderColor: ACCENT, borderWidth: 2 }}>
      <div style={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <p style={{ ...styles.kicker, color: ACCENT }}>Gratuit · fichier Excel</p>
        <h2 id="ou-modele" style={{ ...stepTitle, fontSize: 24 }}>Recevez le modèle Excel complet</h2>
        <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc', fontSize: 16, color: T.text, lineHeight: 1.6 }}>
          <li><strong>Votre produit</strong> : les chiffres ci-dessus, avec toutes les formules.</li>
          <li><strong>Catalogue</strong> : jusqu'à 50 produits, calculés d'un coup. Stock de sécurité, point de commande, quantité à commander.</li>
          <li><strong>Mode d'emploi</strong>, pour choisir le niveau de service et éviter les pièges.</li>
        </ul>
      </div>
      <div style={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12, justifyContent: 'center' }}>
        {state !== 'idle' ? (
          <div role="status" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{state === 'sent' ? `C'est envoyé à ${form.email.trim()}.` : 'Votre fichier est prêt.'}</p>
            {state === 'sending' && <p style={{ ...hint, fontSize: 15 }}>Envoi par e-mail en cours… Vous pouvez déjà le télécharger :</p>}
            {state !== 'sending' && <p style={{ ...hint, fontSize: 15 }}>{state === 'sent' ? "S'il n'arrive pas d'ici quelques minutes, regardez dans les indésirables. Vous pouvez aussi le télécharger tout de suite :" : "L'envoi par e-mail n'a pas abouti. Téléchargez-le ici :"}</p>}
            <button type="button" onClick={download} className="lift" style={{ ...styles.button, border: 0, cursor: 'pointer', alignSelf: 'flex-start' }}>Télécharger le modèle (.xlsx)</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: 12 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <label htmlFor="ou-email" style={label}>E-mail professionnel</label>
                <input id="ou-email" type="email" autoComplete="email" required className="ou-input" style={{ fontFamily: T.sans, fontSize: 16 }} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                <label htmlFor="ou-prenom" style={label}>Prénom <span style={{ fontWeight: 400, color: T.faint }}>· facultatif</span></label>
                <input id="ou-prenom" autoComplete="given-name" className="ou-input" style={{ fontFamily: T.sans, fontSize: 16 }} value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
              </div>
            </div>
            <label className="ou-hp" aria-hidden="true">Site web<input tabIndex={-1} autoComplete="off" value={form.site} onChange={(e) => setForm({ ...form, site: e.target.value })} /></label>
            {error && <p role="alert" style={{ margin: 0, color: RED, fontSize: 15 }}>{error}</p>}
            <button type="submit" disabled={state === 'sending'} className="lift" style={{ ...styles.button, border: 0, cursor: 'pointer', alignSelf: 'flex-start' }}>{state === 'sending' ? 'Envoi…' : 'Recevoir le modèle'}</button>
            <p style={{ ...hint, fontSize: 13 }}>Votre adresse sert à vous envoyer le fichier. Je pourrai vous écrire une fois pour savoir s'il vous a servi, jamais plus, et sans newsletter. <Link to="/mentions-legales">Données personnelles</Link></p>
          </form>
        )}
      </div>
    </section>
  );
};

const H2 = ({ children, id }) => <h2 id={id} style={{ ...styles.h2, marginTop: 56 }}>{children}</h2>;
const P = ({ children }) => <p style={{ margin: '16px 0 0', fontSize: 18, color: T.text, lineHeight: 1.6 }}>{children}</p>;

const EXCEL_ROWS = [
  ['I17', 'Ventes par jour', '=MOYENNE(E2:E13)/7'],
  ['I18', 'Écart-type par jour (hors B2B)', '=ECARTYPE.STANDARD(F2:F13)/RACINE(7)'],
  ['I19', 'Écart-type du délai', '=MAX(0;I7-I6)/3'],
  ['I20', 'Stock de sécurité', '=ARRONDI.SUP(LOI.NORMALE.STANDARD.INVERSE.N(I8)*RACINE(I5*I18^2+I17^2*I19^2);0)'],
  ['I21', 'Point de commande', '=ARRONDI.SUP(I17*I5+I20;0)']
];

const CalculStockSecuritePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <SitePage>
      <SEO
        title="Calcul stock de sécurité et point de commande (gratuit)"
        description="Calculez stock de sécurité et point de commande à partir de vos ventes Amazon et Shopify et du délai fournisseur. Formule détaillée, export Excel."
        path={PATH}
        breadcrumb={[['Outils gratuits', '/outils'], ['Calculateur de stock de sécurité', PATH]]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(APP_LD)}</script>
      </Helmet>

      <section style={{ ...styles.container, padding: '56px 24px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={styles.kicker}>Outil gratuit · sans inscription · mis à jour le {majFr}</p>
        <h1 style={{ ...styles.h1, maxWidth: 900 }}>Calculateur de stock de sécurité et de point de commande</h1>
        <p style={{ ...styles.lead, maxWidth: 720 }}>Combien garder en réserve, et à quel niveau repasser commande. Pensé pour une marque qui vend sur Amazon et sur Shopify depuis le même stock, avec un fournisseur à plusieurs semaines de bateau.</p>
      </section>

      <Calculator />

      <article style={{ ...styles.narrow, paddingTop: 24 }}>
        <H2>Stock de sécurité et point de commande : la différence</H2>
        <P><strong>Le stock de sécurité</strong> est la réserve qui absorbe l'imprévu : une semaine de ventes plus forte, un conteneur bloqué au port. En temps normal, on n'y touche pas.</P>
        <P><strong>Le point de commande</strong>, qu'on appelle aussi stock d'alerte, est le niveau où il faut repasser commande : tout ce que vous vendrez pendant le délai d'approvisionnement, plus le stock de sécurité.</P>
        <P>Dans l'exemple ci-dessus, la gourde se vend {n1(EX.d)} fois par jour et met {n0(EX.lead)} jours à arriver. Pendant ce délai, on en vendra {n0(EX.d * EX.lead)}. Avec {n0(EX.safety)} unités de sécurité, on repasse commande dès que le stock passe sous {n0(EX.reorder)}.</P>

        <H2>La formule du stock de sécurité</H2>
        <div style={{ marginTop: 20, padding: '18px 20px', background: '#FFFFFF', border: `1px solid ${T.line2}`, borderLeft: `4px solid ${ACCENT}`, fontFamily: T.mono, fontSize: 'clamp(15px, 2.2vw, 19px)' }}>Stock de sécurité = z × √(L × σ² + d² × σL²)</div>
        <ul style={{ margin: '16px 0 0', paddingLeft: 20, listStyle: 'disc', fontSize: 17, color: T.text, lineHeight: 1.7 }}>
          <li><strong>d</strong> : ventes moyennes par jour, tous canaux confondus</li>
          <li><strong>σ</strong> : écart-type des ventes par jour hors B2B (de combien elles varient)</li>
          <li><strong>L</strong> : délai d'approvisionnement total, en jours</li>
          <li><strong>σL</strong> : écart-type du délai, environ (délai le plus long − le plus court) ÷ 3</li>
          <li><strong>z</strong> : le niveau de service traduit par la loi normale (1,65 pour 95 %, 2,33 pour 99 %)</li>
        </ul>
        <P>Si votre délai ne varie jamais, la formule se simplifie en z × σ × √L. C'est celle des manuels. Mais avec un fournisseur en Asie, le délai varie presque toujours, et c'est souvent lui qui pèse le plus : dans l'exemple, un délai parfaitement fiable ferait tomber le stock de sécurité de {n0(EX.safety)} à {n0(EX.safetyReliableLead)} unités, quand des ventes parfaitement régulières ne le baisseraient qu'à {n0(EX.safetySteadySales)}.</P>
        <P>Deux précisions. La variabilité des ventes se calcule sur Amazon et le site seulement : les commandes B2B arrivent par gros volumes connus d'avance, elles comptent dans la moyenne, pas dans le hasard. Et celle du délai se déduit du délai le plus court et du plus long constatés : leur écart divisé par trois donne une bonne approximation de l'écart-type.</P>

        <H2>Le délai à compter quand on vend sur Amazon</H2>
        <P>Le délai qui compte va du bon de commande au produit en vente. Pour un vendeur Amazon, il s'additionne vite :</P>
        <ul style={{ margin: '12px 0 0', paddingLeft: 20, listStyle: 'disc', fontSize: 17, color: T.text, lineHeight: 1.7 }}>
          <li>la fabrication chez le fournisseur ;</li>
          <li>le transport, et le dédouanement pour de l'import ;</li>
          <li>la préparation de l'envoi vers Amazon ;</li>
          <li>la réception par les entrepôts Amazon, qui s'allonge en fin d'année.</li>
        </ul>
        <P>Oublier la dernière étape, c'est passer Noël en rupture avec du stock bloqué en réception. Et pour une commande passée entre décembre et février, comptez le Nouvel An chinois : beaucoup d'usines ferment environ trois semaines.</P>

        <H2>Le même calcul sur Excel</H2>
        <P>Ventes hebdomadaires totales en E2:E13, ventes Amazon + site en F2:F13, délai total en I5, délai le plus court et le plus long en I6 et I7, niveau de service en I8 (0,95). Les formules, dans Excel en français :</P>
        <div style={{ marginTop: 20, border: `1px solid ${T.line2}`, borderRadius: 6, overflow: 'hidden', fontFamily: T.mono, fontSize: 13.5, background: '#FFFFFF' }}>
          {EXCEL_ROWS.map(([cell, label, formula], i) => (
            <div key={cell} style={{ display: 'grid', gridTemplateColumns: '48px minmax(120px, 1fr) 2fr', borderTop: i ? `1px solid ${T.line}` : 0 }}>
              <span style={{ padding: '10px 8px', background: '#F7F8FA', color: T.faint, textAlign: 'center', borderRight: `1px solid ${T.line}` }}>{cell}</span>
              <span style={{ padding: '10px 12px', borderRight: `1px solid ${T.line}` }}>{label}</span>
              <span style={{ padding: '10px 12px', color: ACCENT, overflowWrap: 'anywhere' }}>{formula}</span>
            </div>
          ))}
        </div>
        <P>Plus simple : le bouton « Copier pour Excel » du calculateur colle une feuille complète, avec vos chiffres et toutes les formules déjà reliées. Et <a href="#ou-modele">le modèle à recevoir par e-mail</a> ajoute un onglet catalogue, pour faire le calcul sur cinquante produits d'un coup.</P>

        <H2>Ce que ce calcul ne fait pas</H2>
        <P>Il travaille sur un produit à la fois, à partir d'une moyenne. La hausse saisonnière ne vaut que pour la période couverte par la commande : si elle commence avant la livraison, relevez aussi le délai ou le niveau de service. Il ne remplit pas de conteneur, et ne répartit pas le stock entre Amazon et votre entrepôt.</P>
        <P>Sur une dizaine de références, Excel suffit. Sur trois cents, refaire ce calcul chaque lundi à partir des exports Amazon et Shopify devient un poste à mi-temps. C'est exactement ce que fait <Link to="/reappro-prevision-stock">un outil de réappro sur mesure</Link> : le même calcul, sur tout le catalogue, chaque semaine, avec vos délais réels.</P>

        <H2 id="faq">Questions fréquentes</H2>
        <div style={{ marginTop: 20, borderTop: `1px solid ${T.line2}` }}>
          {FAQ.map(([q, a]) => (
            <details key={q} style={{ borderBottom: `1px solid ${T.line2}`, padding: '16px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>{q}</summary>
              <p style={{ margin: '10px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{a}</p>
            </details>
          ))}
        </div>
      </article>

      <CallToAction from="calculateur" title="Ce calcul, sur tout votre catalogue ?" text="30 minutes pour voir si un outil de réappro sur mesure vaut le coup chez vous. Si oui, un prix fixe par écrit sous 3 jours." />
    </SitePage>
  );
};

export default CalculStockSecuritePage;
